import React, { createContext, useContext, useState, useCallback } from 'react';
import { useAuth } from './authContext';

const BusinessEmployeeContext = createContext();

export const useBusinessEmployee = () => {
  const context = useContext(BusinessEmployeeContext);
  if (!context) {
    throw new Error('useBusinessEmployee must be used within a BusinessEmployeeProvider');
  }
  return context;
};

export const BusinessEmployeeProvider = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { makeAuthenticatedRequest, loginType } = useAuth();

  // API base URL for business employee endpoints
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://7d515f15e81f.ngrok-free.app';
  
  // Determine the correct base URL based on login type
  const getEmployeeBaseUrl = useCallback(() => {
    if (loginType === 'master') {
      return `${API_BASE_URL}/master/business-employees/api/v1/business-employees`;
    } else if (loginType === 'admin') {
      return `${API_BASE_URL}/admin/business-employees/api/v1/business-employees`;
    } else {
      throw new Error('Invalid login type for business employees access');
    }
  }, [API_BASE_URL, loginType]);

  // Helper function to handle API responses
  const handleApiResponse = async (response) => {
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || data.message || 'Request failed');
    }
    
    return data;
  };

  // Clear error state
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Validate user access
  const validateAccess = useCallback(() => {
    if (loginType !== 'admin' && loginType !== 'master') {
      throw new Error('Only admin and master users can access business employees');
    }
  }, [loginType]);

  // Get employees by registry number with pagination and filters
  const getEmployeesByRegistryNumber = useCallback(async (registryNumber, options = {}) => {
    validateAccess();
    
    if (!registryNumber) {
      throw new Error('Registry number is required');
    }

    setLoading(true);
    setError(null);

    try {
      const queryParams = new URLSearchParams();
      
      if (options.limit) queryParams.append('limit', options.limit);
      if (options.offset) queryParams.append('offset', options.offset);
      if (options.employeeStatus) queryParams.append('employee_status', options.employeeStatus);
      if (options.employeeGender) queryParams.append('employee_gender', options.employeeGender);
      if (options.employeeVisible !== undefined) queryParams.append('employee_visible', options.employeeVisible);
      if (options.employeeIsOwner !== undefined) queryParams.append('employee_is_owner', options.employeeIsOwner);
      if (options.employeeDocuments !== undefined) queryParams.append('employee_documents', options.employeeDocuments);
      if (options.employeeServices !== undefined) queryParams.append('employee_services', options.employeeServices);
      if (options.startDateFrom) queryParams.append('start_date_from', options.startDateFrom);
      if (options.startDateTo) queryParams.append('start_date_to', options.startDateTo);
      if (options.search) queryParams.append('search', options.search);

      const baseUrl = getEmployeeBaseUrl();
      const url = `${baseUrl}/registry/${encodeURIComponent(registryNumber)}`;
      const fullUrl = queryParams.toString() ? `${url}?${queryParams.toString()}` : url;
      
      const response = await makeAuthenticatedRequest(fullUrl);
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to fetch employees';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getEmployeeBaseUrl]);

  // Get employee statistics by registry number
  const getEmployeeStats = useCallback(async (registryNumber) => {
    validateAccess();
    
    if (!registryNumber) {
      throw new Error('Registry number is required');
    }

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getEmployeeBaseUrl();
      const response = await makeAuthenticatedRequest(`${baseUrl}/registry/${encodeURIComponent(registryNumber)}/stats`);
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to get employee statistics';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getEmployeeBaseUrl]);

  // Get employee statuses by registry number
  const getEmployeeStatuses = useCallback(async (registryNumber) => {
    validateAccess();
    
    if (!registryNumber) {
      throw new Error('Registry number is required');
    }

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getEmployeeBaseUrl();
      const response = await makeAuthenticatedRequest(`${baseUrl}/registry/${encodeURIComponent(registryNumber)}/statuses`);
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to get employee statuses';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getEmployeeBaseUrl]);

  // Get employee by employee ID
  const getEmployeeById = useCallback(async (employeeId) => {
    validateAccess();
    
    if (!employeeId) {
      throw new Error('Employee ID is required');
    }

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getEmployeeBaseUrl();
      const response = await makeAuthenticatedRequest(`${baseUrl}/employee/${encodeURIComponent(employeeId)}`);
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to get employee by ID';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getEmployeeBaseUrl]);

  // Get employees filtered by status
  const getEmployeesByStatus = useCallback(async (registryNumber, status, options = {}) => {
    validateAccess();

    const filterOptions = {
      ...options,
      employeeStatus: status,
    };

    return await getEmployeesByRegistryNumber(registryNumber, filterOptions);
  }, [getEmployeesByRegistryNumber, validateAccess]);

  // Get employees filtered by visibility
  const getEmployeesByVisibility = useCallback(async (registryNumber, isVisible, options = {}) => {
    validateAccess();

    const filterOptions = {
      ...options,
      employeeVisible: isVisible,
    };

    return await getEmployeesByRegistryNumber(registryNumber, filterOptions);
  }, [getEmployeesByRegistryNumber, validateAccess]);

  // Get employees filtered by owner status
  const getEmployeesByOwnerStatus = useCallback(async (registryNumber, isOwner, options = {}) => {
    validateAccess();

    const filterOptions = {
      ...options,
      employeeIsOwner: isOwner,
    };

    return await getEmployeesByRegistryNumber(registryNumber, filterOptions);
  }, [getEmployeesByRegistryNumber, validateAccess]);

  // Get employees by date range
  const getEmployeesByDateRange = useCallback(async (registryNumber, startDateFrom, startDateTo, options = {}) => {
    validateAccess();

    const filterOptions = {
      ...options,
      startDateFrom: startDateFrom,
      startDateTo: startDateTo,
    };

    return await getEmployeesByRegistryNumber(registryNumber, filterOptions);
  }, [getEmployeesByRegistryNumber, validateAccess]);

  // Search employees
  const searchEmployees = useCallback(async (registryNumber, searchTerm, options = {}) => {
    validateAccess();

    const filterOptions = {
      ...options,
      search: searchTerm,
    };

    return await getEmployeesByRegistryNumber(registryNumber, filterOptions);
  }, [getEmployeesByRegistryNumber, validateAccess]);

  // Get active employees only
  const getActiveEmployees = useCallback(async (registryNumber, options = {}) => {
    validateAccess();
    return await getEmployeesByStatus(registryNumber, 'Active', options);
  }, [getEmployeesByStatus, validateAccess]);

  // Get owner employees only
  const getOwnerEmployees = useCallback(async (registryNumber, options = {}) => {
    validateAccess();
    return await getEmployeesByOwnerStatus(registryNumber, true, options);
  }, [getEmployeesByOwnerStatus, validateAccess]);

  // Get employees with documents
  const getEmployeesWithDocuments = useCallback(async (registryNumber, options = {}) => {
    validateAccess();

    const filterOptions = {
      ...options,
      employeeDocuments: true,
    };

    return await getEmployeesByRegistryNumber(registryNumber, filterOptions);
  }, [getEmployeesByRegistryNumber, validateAccess]);

  // Get employees with services
  const getEmployeesWithServices = useCallback(async (registryNumber, options = {}) => {
    validateAccess();

    const filterOptions = {
      ...options,
      employeeServices: true,
    };

    return await getEmployeesByRegistryNumber(registryNumber, filterOptions);
  }, [getEmployeesByRegistryNumber, validateAccess]);

  // Export employees data (prepare data for CSV/Excel export)
  const exportEmployeesData = useCallback(async (registryNumber, filters = {}) => {
    validateAccess();

    // Get all employees without pagination for export
    const exportOptions = {
      ...filters,
      limit: 10000, // Large limit for export
      offset: 0,
    };

    const result = await getEmployeesByRegistryNumber(registryNumber, exportOptions);
    
    // Transform data for export
    const exportData = result.employees.map(employee => ({
      'Employee ID': employee.employee_id,
      'First Name': employee.employee_first_name || '',
      'Last Name': employee.employee_last_name || '',
      'Email': employee.employee_email || '',
      'Phone': employee.employee_phone || '',
      'Start Date': employee.employee_start_date ? new Date(employee.employee_start_date).toLocaleDateString() : '',
      'Departure Date': employee.employee_departure_date ? new Date(employee.employee_departure_date).toLocaleDateString() : '',
      'Status': employee.employee_status,
      'Is Owner': employee.employee_is_owner ? 'Yes' : 'No',
      'Has Documents': employee.employee_documents ? 'Yes' : 'No',
      'Provides Services': employee.employee_services ? 'Yes' : 'No',
      'Visible': employee.employee_visible ? 'Yes' : 'No',
      'Enrollment Date': employee.employee_enrollment_date ? new Date(employee.employee_enrollment_date).toLocaleDateString() : '',
    }));

    return {
      data: exportData,
      totalCount: result.total_count,
      registryNumber: registryNumber,
      filename: `employees_${registryNumber}_${new Date().toISOString().split('T')[0]}.csv`
    };
  }, [getEmployeesByRegistryNumber, validateAccess]);

  // Health check for business employees service
  const checkHealth = useCallback(async () => {
    validateAccess();

    try {
      const healthUrl = loginType === 'master' 
        ? `${API_BASE_URL}/master/business-employees/health`
        : `${API_BASE_URL}/admin/business-employees/health`;
      
      const response = await makeAuthenticatedRequest(healthUrl);
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Health check failed';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, [makeAuthenticatedRequest, validateAccess, API_BASE_URL, loginType]);

  // Validation helpers
  const validateEmployeeFilters = useCallback((filters) => {
    const errors = [];

    if (filters.limit && (filters.limit < 1 || filters.limit > 1000)) {
      errors.push({ field: 'limit', message: 'Limit must be between 1 and 1000' });
    }

    if (filters.offset && filters.offset < 0) {
      errors.push({ field: 'offset', message: 'Offset must be non-negative' });
    }

    if (filters.startDateFrom && filters.startDateTo && new Date(filters.startDateFrom) > new Date(filters.startDateTo)) {
      errors.push({ field: 'dateRange', message: 'Start date from must be before start date to' });
    }

    if (filters.employeeStatus && !['Active', 'Inactive', 'Terminated', 'On Leave'].includes(filters.employeeStatus)) {
      errors.push({ field: 'employeeStatus', message: 'Invalid employee status' });
    }

    if (filters.search && filters.search.length < 2) {
      errors.push({ field: 'search', message: 'Search term must be at least 2 characters' });
    }

    return errors;
  }, []);

  // Format employee data for display
  const formatEmployeeData = useCallback((employee) => {
    const getEmployeeTenure = () => {
      if (!employee.employee_start_date) return 'N/A';
      const startDate = new Date(employee.employee_start_date);
      const endDate = employee.employee_departure_date ? new Date(employee.employee_departure_date) : new Date();
      const diffTime = Math.abs(endDate - startDate);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      const years = Math.floor(diffDays / 365);
      const months = Math.floor((diffDays % 365) / 30);
      
      if (years > 0) {
        return `${years} year${years > 1 ? 's' : ''}, ${months} month${months > 1 ? 's' : ''}`;
      } else if (months > 0) {
        return `${months} month${months > 1 ? 's' : ''}`;
      } else {
        return `${diffDays} day${diffDays > 1 ? 's' : ''}`;
      }
    };

    return {
      ...employee,
      fullName: `${employee.employee_first_name || ''} ${employee.employee_last_name || ''}`.trim() || 'N/A',
      statusColor: employee.employee_status === 'Active' ? 'green' :
                   employee.employee_status === 'Inactive' ? 'orange' :
                   employee.employee_status === 'Terminated' ? 'red' :
                   employee.employee_status === 'On Leave' ? 'blue' : 'gray',
      visibilityText: employee.employee_visible ? 'Visible' : 'Hidden',
      visibilityColor: employee.employee_visible ? 'green' : 'red',
      ownerText: employee.employee_is_owner ? 'Owner' : 'Employee',
      ownerColor: employee.employee_is_owner ? 'purple' : 'blue',
      documentsText: employee.employee_documents ? 'Has Documents' : 'No Documents',
      documentsColor: employee.employee_documents ? 'green' : 'gray',
      servicesText: employee.employee_services ? 'Provides Services' : 'No Services',
      servicesColor: employee.employee_services ? 'green' : 'gray',
      formattedStartDate: employee.employee_start_date ? new Date(employee.employee_start_date).toLocaleDateString() : 'N/A',
      formattedDepartureDate: employee.employee_departure_date ? new Date(employee.employee_departure_date).toLocaleDateString() : null,
      formattedEnrollmentDate: employee.employee_enrollment_date ? new Date(employee.employee_enrollment_date).toLocaleDateString() : 'N/A',
      tenure: getEmployeeTenure(),
      isCurrentEmployee: !employee.employee_departure_date,
    };
  }, []);

  // Get employee summary for a business
  const getEmployeeSummary = useCallback(async (registryNumber) => {
    validateAccess();

    try {
      const [stats, employees] = await Promise.all([
        getEmployeeStats(registryNumber),
        getEmployeesByRegistryNumber(registryNumber, { limit: 10 }) // Get sample employees
      ]);

      return {
        registryNumber,
        totalEmployees: stats.stats.total_employees,
        activeEmployees: stats.stats.active_employees,
        inactiveEmployees: stats.stats.inactive_employees,
        visibleEmployees: stats.stats.visible_employees,
        hiddenEmployees: stats.stats.hidden_employees,
        ownerEmployees: stats.stats.owner_employees,
        withDocuments: stats.stats.with_documents,
        withServices: stats.stats.with_services,
        averageTenure: stats.stats.average_employee_tenure_days,
        employeesByStatus: stats.stats.employees_by_status,
        employeesByGender: stats.stats.employees_by_gender,
        sampleEmployees: employees.employees,
        accessLevel: loginType,
        hasEmployees: stats.stats.total_employees > 0,
        activeRate: stats.stats.total_employees > 0 
          ? (stats.stats.active_employees / stats.stats.total_employees * 100).toFixed(1) + '%'
          : '0%',
        ownerRate: stats.stats.total_employees > 0 
          ? (stats.stats.owner_employees / stats.stats.total_employees * 100).toFixed(1) + '%'
          : '0%',
        averageTenureFormatted: stats.stats.average_employee_tenure_days 
          ? `${Math.round(stats.stats.average_employee_tenure_days)} days`
          : 'N/A',
      };
    } catch (err) {
      const errorMessage = err.message || 'Failed to get employee summary';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, [getEmployeeStats, getEmployeesByRegistryNumber, validateAccess, loginType]);

  // Get employee demographics
  const getEmployeeDemographics = useCallback(async (registryNumber) => {
    validateAccess();

    try {
      const stats = await getEmployeeStats(registryNumber);
      
      return {
        registryNumber,
        genderDistribution: stats.stats.employees_by_gender,
        statusDistribution: stats.stats.employees_by_status,
        totalEmployees: stats.stats.total_employees,
        diversityMetrics: {
          genderDiversity: Object.keys(stats.stats.employees_by_gender).length,
          statusVariety: Object.keys(stats.stats.employees_by_status).length,
        }
      };
    } catch (err) {
      const errorMessage = err.message || 'Failed to get employee demographics';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, [getEmployeeStats, validateAccess]);

  // Get current endpoint info for debugging
  const getEndpointInfo = useCallback(() => {
    return {
      loginType,
      baseUrl: loginType === 'master' 
        ? `${API_BASE_URL}/master/business-employees/api/v1/business-employees`
        : `${API_BASE_URL}/admin/business-employees/api/v1/business-employees`,
      accessLevel: loginType === 'master' ? 'Master Access' : 'Admin Access',
      requiredHeaders: loginType === 'master' 
        ? ['Authorization', 'secret_credential']
        : ['Authorization', 'admin_id'],
    };
  }, [loginType, API_BASE_URL]);

  const value = {
    // State
    loading,
    error,
    
    // Core Actions
    getEmployeesByRegistryNumber,
    getEmployeeStats,
    getEmployeeStatuses,
    getEmployeeById,
    
    // Search and Filter Actions
    getEmployeesByStatus,
    getEmployeesByVisibility,
    getEmployeesByOwnerStatus,
    getEmployeesByDateRange,
    searchEmployees,
    getActiveEmployees,
    getOwnerEmployees,
    getEmployeesWithDocuments,
    getEmployeesWithServices,
    
    // Utility Actions
    exportEmployeesData,
    checkHealth,
    getEmployeeSummary,
    getEmployeeDemographics,
    
    // Helpers
    clearError,
    validateEmployeeFilters,
    formatEmployeeData,
    getEndpointInfo,
    
    // Computed values
    isAdminUser: loginType === 'admin',
    isMasterUser: loginType === 'master',
    hasEmployeesAccess: loginType === 'admin' || loginType === 'master',
    currentAccessLevel: loginType,
  };

  return (
    <BusinessEmployeeContext.Provider value={value}>
      {children}
    </BusinessEmployeeContext.Provider>
  );
};